import * as PIXI from "pixi.js-legacy";

import CommanderBase from "../framework/src/commander/CommanderBase";
import events from "../framework/src/events/EventsObservable";
import States from "./States";

import InfoPages from "./InfoPages";
import TouchZone from "./TouchZone";

import { WALLET_EVENTS } from "../framework/src/wallet/Wallet";
import { REELS_EVENTS } from "../framework/src/game/slot/reelsEvents";
import AddResult from "../framework/src/connect/result/AddResult";
import SERVICE_IDENTIFIER from "../framework/src/constants/identifiers";
import GAME_PARTS from "./identifiers";
import GameSlots from "../framework/src/game/slot/GameSlots";
import GameFreespins from "../framework/src/game/GameFreespins";
import { GAME_EVENTS } from "../framework/src/game/gameEvents";
import { Container, inject, injectable } from "inversify";
import System from "../framework/src/system/System";
import SystemConfig, {
	CONFIG_PARAMS,
	GAMECONF_PARAMS,
} from "../framework/src/system/SystemConfig";
import IEngine from "../framework/src/engine/IEngine";
import ISlots from "../framework/src/game/slot/ISlots";
import MadmaxPixiLayer from "../framework/src/engine/pixi/MadmaxPixiLayer";
import { getSprite } from "../framework/src/modules/Sprite";
// import { EASE, getTween, getTweenAsync } from '../framework/src/modules/Tween';
import IdleAnimation from "./Animations/IdleAnimation";
import {
	pixiClearTimeout,
	pixiSetTimeout,
	pixiSetTimeoutAsync,
} from "../framework/src/engine/pixi/pixiTimeout";
import WinController from "./WinController";
import FreeSpins from "./Animations/FreeSpins";
import ReSpin from "./Animations/ReSpin";
import { STATES } from "../framework/src/states/states";
import { Frames } from "./Code/Frames";
import Board from "./Animations/Board";
import Win from "./Animations/Win";
import BigWin from "./Animations/BigWin";
import PixiAudio from "../framework/src/engine/pixi/PixiAudio";
import IInfoPages from "../framework/src/engine/IInfoPages";

/**
 *
 * @extends CommanderBase
 * @class
 */
@injectable()
class Commander extends CommanderBase {
	slots: ISlots;
	config: SystemConfig;
	layers: Record<string, MadmaxPixiLayer>;

	InfoPages: InfoPages;
	TouchZone: TouchZone;
	GameFreespins: GameFreespins;

	IdleAnimation: IdleAnimation;
	Board: Board;

	previousState: string;
	currentState: string;

	result: AddResult;

	audio: PixiAudio;

	skipAnim: boolean;
	lineWinIndex: number = 0;
	scatterCount: number = 1;

	WinController: WinController;
	FreeSpins: FreeSpins;
	ReSpin: ReSpin;
	Win: Win;
	BigWin: BigWin;

	reelsData: Array<any> = [];
	reelDelay: {
		searchIndex0: number[];
		searchIndex1: number[];
		searchIndex2: number[];
	} = {
		searchIndex0: [],
		searchIndex1: [],
		searchIndex2: [],
	};

	// foreground position coordinates for ropes, woods and their' Y from top
	foregroundObjects: PIXI.Sprite[] = [];
	ropesPos: number[] = [];

	specifics: SystemConfig;

	constructor(
		@inject(SERVICE_IDENTIFIER.System) system: System,
		@inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
		@inject(SERVICE_IDENTIFIER.Game) game: GameSlots,
		@inject(SERVICE_IDENTIFIER.Engine) engine: IEngine,
		@inject(SERVICE_IDENTIFIER.ParagraphPage) protected paragraph: IInfoPages
	) {
		super(system, game, engine);

		// @ts-ignore
		this.slots = game.gameCore.slots;
		this.GameFreespins = game.freespins;
		// @ts-ignore
		this.layers = this.app.layers.getAllLayers();

		this.config = config;

		// @ts-ignore
		// window.commanderXD = this;
		// pomucka, kdyz v chromu napises do konzoly commanderXD, budes mit pristup ke vsemu a muzes si to vypsat
		// console.log("%c CONFIG %o CONFIG ", "background-color:red", this.config);
	}

	/**
	 * Initialize states, touchzone and all the modules for animations. Creates Logo, Top and black sprites.
	 * @override
	 */
	create(container: Container) {
		super.create(container);

		createjs.MotionGuidePlugin.install(); // without this moving on a bezier curve is not working

		this.TouchZone = container.get<TouchZone>(GAME_PARTS.TouchZone); // temporary disabled
		// this.InfoPages = container.get<InfoPages>(GAME_PARTS.InfoPages);
		this.InfoPages = container.get<InfoPages>(SERVICE_IDENTIFIER.InfoPages);
		this.IdleAnimation = container.get<IdleAnimation>(GAME_PARTS.IdleAnimation);
		this.WinController = container.get<WinController>(GAME_PARTS.WinController);

		this.Board = container.get<Board>(GAME_PARTS.Board);
		this.FreeSpins = container.get<FreeSpins>(GAME_PARTS.FreeSpins);
		this.ReSpin = container.get<ReSpin>(GAME_PARTS.ReSpin);
		this.Win = container.get<Win>(GAME_PARTS.IdleWin);
		this.BigWin = container.get<BigWin>(GAME_PARTS.BigWin);

		this.previousState = "idle";
		this.currentState = "idle";
		this.result = undefined;

		this.skipAnim = false;

		this.onInit();
	}

	notify(eventName: string, data: any) {
		switch (eventName) {
			case WALLET_EVENTS.WALLET_ON_SET_BET:
				this.TouchZone.updateBet();
				break;
		}

		super.notify(eventName, data);
	}

	skip() {
		this.skipAnim = true;
	}

	onInit() {
		Frames.init();

		this.initLogo();
		this.IdleAnimation.init();
		this.initBackground();
		this.addToIdleAnimation();
		this.initForeground();

		this.initFreeSpinAnimation();
		this.initReSpinAnimation();
		this.initWinAnimations();

		this.initStates();

		// this.addInfoPages();

		this.handleState();

		this.audio.playTrack("bg_Ambient");
		pixiSetTimeout(0.5, () => {
			if (!this.audio.isPlaying("bg_Music")) {
				this.audio.playTrack("bg_Music");
			}
		});
	}

	setState(newState: string) {
		this.cleanUp().then(() => {
			this.previousState = this.currentState;
			this.currentState = newState;
			// console.log(`old > ${this.previousState}\nnew > ${this.currentState}`);
			this.handleState();
		});
	}

	async handleState() {
		// console.warn(" this config > ",this.config);
		if (this.result != undefined && this.result.lineWins != undefined) {
			// console.groupCollapsed(" handle state > ");
			// console.log("%c this result > ", "background-color:yellow", this.result);
			// console.log("this result > line wins > ", this.result["result"]["gameSpecifics"]);
			// console.log(this.result.lineWins);
			// console.groupEnd();
		}

		switch (this.currentState) {
			case "idle":
				if (this.GameFreespins.isInFreeSpins()) {
					break;
				}

				if (this.audio.isPlaying("bg_Music")) {
					this.audio.fadeToTrack("bg_Music", 1, 200);
				} else {
					this.audio.playTrack("bg_Music");
				}

				this.result = undefined;
				break;
			case "spin":
				if (this.GameFreespins.isInFreeSpins()) {
					if (!this.FreeSpins.freeSpinBoard.visible) {
						this.FreeSpins.showFreeSpinBoard();
					}

					if (!this.ReSpin.visible) {
						this.ReSpin.visible = false;
						try {
							await this.ReSpin.clearChildList();
						} catch (er) {
							console.error(er);
						}
					}

					this.FreeSpins.updateFreeSpinCount(
						this.GameFreespins.getRoundNumber(),
						this.result.data.result.freeSpinNumber
					);
				}
				break;
			case "win":
				this.states.setState(STATES.SKIP);
				let factor = this.result.win / this.wallet.getBet();
				let winSum = this.result.win;

				if (this.GameFreespins.isInFreeSpins()) {
					if (this.FreeSpins.freeSpinBoard.visible) {
						this.FreeSpins.hideFreeSpinBoard();
					}

					if (this.ReSpin.visible) {
						this.ReSpin.visible = false;
						try {
							await this.ReSpin.clearChildList();
						} catch (err) {
							console.error(err);
						}
					}

					if (this.audio.isPlaying("bg_FreeSpins_Loop")) {
						this.audio.fadeToTrack("bg_FreeSpins_Loop", 0.5, 200);
					}

					if (this.result.win != this.FreeSpins.lastWin) {
						winSum -= this.FreeSpins.lastWin;
						factor = winSum / this.wallet.getBet();
						this.FreeSpins.lastWin = this.result.win;
					}
				} else {
					if (this.audio.isPlaying("bg_Music")) {
						this.audio.fadeToTrack("bg_Music", 0.5, 200);
					}
				}

				if (factor > this.BigWin.bigWinFactors.bigWin) {
					this.BigWin.setBWScoreText(
						this.wallet.moneyToCredit(winSum).toString()
					);
					this.setState("bigWin");

					break;
				}

				this.lineWinIndex = await this.WinController.run(
					this.result.lineWins,
					this.lineWinIndex
				);

				if (
					this.GameFreespins.isInFreeSpins() &&
					!this.FreeSpins.freeSpinBoard.visible
				) {
					this.FreeSpins.showFreeSpinBoard();
					if (this.audio.isPlaying("bg_FreeSpins_Loop")) {
						this.audio.fadeToTrack("bg_FreeSpins_Loop", 1, 200);
					}
				} else {
					if (this.audio.isPlaying("bg_Music")) {
						this.audio.fadeToTrack("bg_Music", 1, 200);
					}
				}

				this.setStateByLineWin(this.result.lineWins[this.lineWinIndex]);

				break;
			case "freespin":
				this.states.setState(STATES.BONUS_WIN);
				this.Board.hide();

				if (this.audio.isPlaying("bg_Music")) {
					this.audio.fadeOutTrack("bg_Music", 200, true);
				}

				await this.FreeSpins.startFreeSpin(this.result, this.lineWinIndex);

				this.GameFreespins.run(this.result);
				this.setStateByLineWin(this.result.lineWins[++this.lineWinIndex]);

				break;
			case "respin":
				if (this.audio.isPlaying("bg_Music")) {
					this.audio.fadeOutTrack("bg_Music", 200, true);
				}

				if (this.audio.isPlaying("bg_FreeSpins_Loop")) {
					this.audio.fadeToTrack("bg_FreeSpins_Loop", 0.5, 200);
				}

				await this.ReSpin.run(this.result, this.lineWinIndex);
				await this.spinRespin(this.result);

				this.setStateByLineWin(this.result.lineWins[++this.lineWinIndex]);
				break;
			case "bigWin":
				pixiSetTimeoutAsync(0.6).then(async () => {
					await this.BigWin.shakeScreenAsync();
				});
				await this.IdleAnimation.runBigWinLightning(2);

				this.lineWinIndex = await this.WinController.run(
					this.result.lineWins,
					this.lineWinIndex
				);
				// events.dispatch(GAME_EVENTS.BIG_WIN)

				if (
					this.GameFreespins.isInFreeSpins() &&
					!this.FreeSpins.freeSpinBoard.visible
				) {
					await this.FreeSpins.showFreeSpinBoard();
				}

				await this.BigWin.bigWinAnimation();
				await this.BigWin.bigWinFadeOut();

				this.setStateByLineWin(this.result.lineWins[this.lineWinIndex]);
				break;
		}
	}

	// clean up after certain state
	async cleanUp() {
		return new Promise((res) => {
			switch (this.currentState) {
				case "idle":
					break;

				case "spin":
					break;

				case "win":
					break;

				case "bigWin":
					break;

				case "showPosition":
					break;

				case "showSymbols":
					break;
			}
			res(" it's clean ");
		});
	}

	controlResult(result: AddResult) {
		// cleans reels data array , filled in states
		this.reelsData = [];
		this.reelsData.length = 0;

		this.specifics = undefined;

		this.result = result;
		this.game.round.resetResultIndex();

		if (result.lineWins && result.lineWins.length) {
			this.setStateByLineWin(result.lineWins[0]);
		} else {
			this.endRound();
		}
	}

	setStateByLineWin(lineWin: any) {
		if (lineWin) {
			switch (lineWin.type) {
				case "normal":
				case "wild":
					this.setState("win");
					break;
				case "freeSpins":
					this.setState("freespin");
					break;
				case "showPosition":
					break;
				case "showSymbols":
					this.setState("respin");
					break;
				case "undefined":
				default:
					this.endRound();
					break;
			}
		} else {
			this.endRound();
		}
	}

	onSpinStart() {
		this.setState("spin");
		this.TouchZone.zonesOff();
		this.TouchZone.hide();
	}

	/**
	 * Call {@link CommanderBase#endRound} to tell server round has ended. After that
	 * clean up after animations and run Idle animation or start autospin, toggle arrows and touchzones.
	 * Reset values for animations and set win.
	 * @param result
	 */
	onEndRoundRecalced({ autostart }: { autostart?: any }) {
		this.setState("idle");
		this.lineWinIndex = 0;
		this.scatterCount = 1;
		this.skipAnim = false;
		this.TouchZone.zonesOn();
	}

	/* * * * * * * * * * * * * *
	 * INITIALIZATION METHODS  *
	 * * * * * * * * * * * * * */
	initLogo() {
		let logo = getSprite("logo", 631, 33); //  629, 33); //644, 38);
		logo.name = "Logo Viking Dice Img";
		this.Board.loadData();

		this.layers.logo.addChild(logo, this.Board.board);
	}

	initBackground() {
		let bg = getSprite("background", 0, 0, { x: 0, y: 0 });
		bg.name = "Background Background";
		this.layers.background.addChild(bg);
	}

	addToIdleAnimation() {
		this.layers.idleAnim.addChild(...this.IdleAnimation.getChildren());
		this.IdleAnimation.run();
	}

	initFreeSpinAnimation() {
		// number of objects that "fire rope animation" needs to be created for
		// (banage have to be subtracted -- need to think of better version then hardcoded)
		this.FreeSpins.init(this.foregroundObjects.length - 4);

		this.layers.logo.addChild(
			this.FreeSpins.freeSpinBoard,
			this.FreeSpins.fireText
		);
		this.layers.emitter.addChild(this.FreeSpins.fireScreen);

		// adding fire rope animation on each rope/wood as a child
		for (let i = 0; i < this.FreeSpins.fireRopeList.length; i++) {
			(this.layers.foreground.children[i] as PIXI.Sprite).addChild(
				this.FreeSpins.fireRopeList[i]
			);
		}
	}

	initReSpinAnimation() {
		this.layers["symbolAnimations"].addChild(this.ReSpin);
	}

	initWinAnimations() {
		this.BigWin.init();
		this.layers["bigWin"].addChild(
			this.Win.backgroundImg,
			this.Win.score,
			this.BigWin.bwCont
		);
	}

	initForeground() {
		let cover = [];
		let woodsPos = [22, 1257];
		this.ropesPos = [280, 520, 760, 1000];
		/*        
            let betweenReelsPosX = [280, 520, 760, 1000];
            let woodPosX = [24, 1257];
            let ropPosY = 340;
        */

		/*
		 * get the position of lower cover (reels_cover_02.png) and it's height
		 * and calculate the position of bandages on it
		 */
		cover.push(getSprite("reels_cover_01", 0, 0, { x: 0, y: 0 }));
		cover[cover.length - 1].name = "Foreground Cover Top";
		cover.push(getSprite("reels_cover_02", 0, 494, { x: 0, y: 0 }));
		cover[cover.length - 1].name = "Foreground Cover Bottom";

		let bandagePosY = Number.parseInt(cover[1].y + cover[1].height * 0.7);
		let ropes = [];
		let bandage = [];

		for (let i = 0; i < this.ropesPos.length; i++) {
			let tmpRope = getSprite("rope", this.ropesPos[i], 0, { x: 0.5, y: 0 });
			tmpRope.name = "Foreground Rope " + i;
			ropes.push(tmpRope);

			let tmpBandage = getSprite(
				"cover_bandage",
				this.ropesPos[i],
				bandagePosY
			);
			tmpBandage.name = "Foreground Bandage " + i;
			bandage.push(tmpBandage);
		}

		let wood = "column";
		let leftWood = getSprite(wood, woodsPos[0], 0, { x: 0.5, y: 0 });
		leftWood.name = "Foreground Left Wood";

		let rightWood = getSprite(wood, woodsPos[1], 0, { x: 0.5, y: 0 });
		rightWood.name = "Foreground Right Wood";

		rightWood.scale.x = -1;

		// list of objects that will be turned off and on , when the info page is viewed
		this.foregroundObjects = [leftWood, ...ropes, rightWood, ...bandage];
		this.layers.foreground.addChild(
			leftWood,
			...ropes,
			rightWood,
			...cover,
			...bandage
		);
	}

	initStates() {
		let states = new States(this);
		this.fillScatterDelayArray();

		events.subscribe(GAME_EVENTS.GAME_SPIN_START, () => this.onSpinStart());
		events.subscribe(GAME_EVENTS.GAME_END_ROUND_RECALCED, (eventName, data) =>
			this.onEndRoundRecalced(data)
		);
		events.subscribe(GAME_EVENTS.GAME_FREESPIN_END, (eventName, { ended }) =>
			this.endFreespins(ended)
		);
		// events.subscribe(GAME_EVENTS.GAME_ADD_RESULT, (eventName, { symbolsMatrix }) => {});

		events.subscribe(REELS_EVENTS.REEL_MOVE, (eventName, data) =>
			states.move.call(this, data)
		);
		events.subscribe(REELS_EVENTS.REEL_STOP, (eventName, data) =>
			states.stop.call(this, data)
		);
		events.subscribe(REELS_EVENTS.REEL_STOPPING, (eventName, data) =>
			states.stopping.call(this, data)
		);
		events.subscribe(REELS_EVENTS.REEL_ACC_DOWN, (eventName, data) =>
			states.accDown.call(this, data)
		);
		events.subscribe(REELS_EVENTS.REEL_SPIN, (eventName, data) =>
			states.spin.call(this, data)
		);
		events.subscribe(REELS_EVENTS.REEL_BRAKEDOWN, (eventName, data) =>
			states.brakeDown.call(this, data)
		);
		events.subscribe(REELS_EVENTS.REEL_BRAKEUP, (eventName, data) =>
			states.brakeUp.call(this, data)
		);
		events.subscribe(REELS_EVENTS.REEL_ACC_UP, (eventName, data) =>
			states.accUp.call(this, data)
		);
		/*        
        // events.subscribe(REELS_EVENTS.REEL_ACC_DOWN, (eventName, data) =>
            // states.accDown.call(this, data)
        // );
*/
		// events.subscribe(REELS_EVENTS.REELS_START, (eventName, data) => {});

		events.subscribe(WALLET_EVENTS.WALLET_ON_SET_BET, this);
	}

	hazScatter(reel) {
		return reel._resSymbols.find(
			(symbol) => symbol.symbolName === "symbol_Scatter"
		);
	}

	fillScatterDelayArray() {
		// because reels length are loaded later this needs to be in init
		let tempReelsLength = this.config.get(CONFIG_PARAMS.REELS).length;

		let betweenReelStops = this.config.get(CONFIG_PARAMS.BEHAVIOR)["reel"][
			"spin"
		]["delayBetweenReelStops"];
		let scatterDelay = this.config.get(CONFIG_PARAMS.BEHAVIOR)["reel"]["spin"][
			"scatterDelay"
		];
		let scatterDuration = this.config.get(CONFIG_PARAMS.BEHAVIOR)["reel"][
			"spin"
		]["scatterDuration"];
		let delayNDuration =
			this.config.get(CONFIG_PARAMS.BEHAVIOR)["reel"]["spin"]["scatterDelay"] +
			this.config.get(CONFIG_PARAMS.BEHAVIOR)["reel"]["spin"][
				"scatterDuration"
			];

		for (let i = 0; i < tempReelsLength; i++) {
			let delay = betweenReelStops * i;
			this.reelDelay.searchIndex0.push(delay);

			if (i > 1) {
				// for each reel with ID 2 and higher we need to add to delay the multiplication of "delay between reel stops" for each reel higher
				delay = delayNDuration;

				if (i > 2) {
					delay += betweenReelStops;
					if (i % 3) {
						delay += betweenReelStops * 2;
					}
				}
			}

			this.reelDelay.searchIndex1.push(delay);

			if (i > 2) {
				// storm searching animation goes animates on the next reel , reel id 1 it goes to 2
				delay = delayNDuration * 2 + ((betweenReelStops * i) % 3);
			}
			this.reelDelay.searchIndex2.push(delay);
		}
	}

	async endFreespins(end) {
		if (end) {
			await this.FreeSpins.endFreeSpinMode().then(() => {
				this.GameFreespins.end();
			});
		}
	}

	// addInfoPages() {
	//     this.InfoPages.layer = this.app["layers"].getLayer("info");
	//     this.InfoPages.addInfoPages(this.TouchZone, this.foregroundObjects);
	// }

	/*_______________________________________________
    // STATIC METHODS
    */

	static cleanArray(array: Array<any>) {
		array.length = 0;
	}
}
export default Commander;
